import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ArrowRight, Sparkles, CheckCircle2, XCircle, Trophy, GraduationCap, Users2, School, FlaskConical, Repeat, Cloud, Rocket, Layers, Globe2, Award, Medal } from "lucide-react";
import { KineticHeading, Reveal } from "@/components/Reveal";
import { SectionLabel } from "@/components/SectionLabel";
import { Marquee } from "@/components/Marquee";
import { Stat, CTASection } from "@/components/Bits";
import { FloatingQuote } from "@/components/FloatingQuote";
import { IMG, PULL_QUOTE, PRESS } from "@/data/content";

// Student experiment shots (from the client's uploaded Concept Lab assets) — auto-advancing hero slideshow
const HERO_SLIDES = [
  "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/pxzaux2x_Screenshot%202026-01-31%20153409.png",
  "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/mshg4mb2_Screenshot%202026-01-31%20155637.png",
  "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/25b7tp2q_Screenshot%202026-01-31%20155616.png",
  "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/2cztet2t_Screenshot%202026-01-31%20155353.png",
  "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/xby25s6p_Screenshot%202026-01-31%20155446.png",
  "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/kpnyx3tb_Screenshot%202026-01-31%20153618.png",
];

const SLIDE_CAPTIONS = [
  "Students recording live data during a Concept Lab experiment",
  "Every learner at the apparatus — not watching, doing",
  "Teacher demonstration: the concept made visible before it is verified",
  "Measurement and observation — evidence before conclusions",
  "Investigating in teams, verifying independently",
  "From experiment to problem-solving, in the same lesson",
];

const TRANSFORM_ROWS = [
  { trad: "All lectures, no labs", pbl: "All labs, no lectures" },
  { trad: "Classrooms with just blackboards", pbl: "Concept Labs with scientific apparatus" },
  { trad: "Chalk-and-talk", pbl: "Demonstrations & experiments using apparatus" },
  { trad: "Teacher explains", pbl: "Teacher demonstrates" },
  { trad: "Students listen", pbl: "Students experiment" },
  { trad: "Learning is assumed", pbl: "Learning is verified" },
  { trad: "Concepts are memorised", pbl: "Concepts are developed" },
  { trad: "Answers are reproduced", pbl: "Problems are solved independently" },
  { trad: "Assessment comes later", pbl: "Correction happens immediately" },
];

const SYSTEM_PARTS = [
  { icon: Layers, hex: "#2E6BB0", t: "PBL-TDLE Framework", d: "The complete academic transformation system.", to: "/framework" },
  { icon: FlaskConical, hex: "#1E9E8A", t: "Concept Labs", d: "Classrooms equipped for demonstrations and student experiments.", to: "/concept-labs" },
  { icon: Repeat, hex: "#8B5CF6", t: "IDEA Pedagogy", d: "Identify → Develop → Execute → Assess.", to: "/framework" },
  { icon: Cloud, hex: "#F0A831", t: "CoPE-App & Learner App", d: "Lesson planning, experiments, assessments and real-time learning evidence.", to: "/framework" },
  { icon: Rocket, hex: "#E11D66", t: "NextGen Training Program", d: "Advanced problem-solving, competitions, research and global university preparation.", to: "/ntp" },
];

const OUTCOME_CARDS = [
  { icon: Trophy, hex: "#F0A831", t: "100% Cambridge examination results", d: "Every candidate cleared their Cambridge board examinations." },
  { icon: Medal, hex: "#2AA7D4", t: "International Rank 1 — twice", d: "Two International Rank 1 finishes and 1,500+ gold medals across 10 years of SOF International Olympiads." },
  { icon: Award, hex: "#8B5CF6", t: "BPhO, AMC, PhysicsBowl & BIOlympiad", d: "Entire Grade 11 class medalled at the British Physics Olympiad; 8 of 13 honoured at AMC; medals at PhysicsBowl and BIOlympiad." },
  { icon: GraduationCap, hex: "#1E9E8A", t: "NEET & JEE success — no external coaching", d: "Problem-based classrooms prepared students for India's toughest entrance exams." },
  { icon: Globe2, hex: "#2E6BB0", t: "International university admissions", d: "Students admitted to leading universities in the UK and USA." },
  { icon: Sparkles, hex: "#E11D66", t: "Major scholarships & research opportunities", d: "Three 100% scholarships in two years — Rose-Hulman, Nottingham, Lancaster." },
];

const AUDIENCES = [
  { icon: School, hex: "#2E6BB0", who: "School Leaders", label: "Transform Your School", to: "/your-journey" },
  { icon: Users2, hex: "#1E9E8A", who: "Teachers", label: "Become a Research-Educator", to: "/learning-redefined" },
  { icon: CheckCircle2, hex: "#F0A831", who: "Parents", label: "See How Learning Is Verified", to: "/framework" },
  { icon: Rocket, hex: "#8B5CF6", who: "Students", label: "Explore NTP & Advanced Problem-Solving", to: "/ntp" },
];

const HOME_TESTIMONIALS = [
  { q: "Instead of simply learning concepts through textbooks, we understand them through demonstrations, experiments, investigations and problem solving. This has helped me develop confidence, analytical thinking and a much deeper understanding of science and mathematics.", a: "Mukta Joshi", r: "Student · 9 years at The Creator International School" },
  { q: "Students do not simply memorize concepts but actually develop them through demonstrations, experiments and advanced problem-solving. This has greatly improved my daughter's understanding, confidence and ability to apply concepts in real life.", a: "Anand Sharma", r: "Parent · The Creator International School" },
  { q: "In 16+ years of teaching across three states, this is the most innovative educational environment I have experienced. Education here goes beyond memorization and focuses on real understanding and lifelong learning skills.", a: "Mangala Mahajan", r: "Teacher (M.A. English, M.Ed.) · 16+ Years Experience" },
];

const PREVIEWS = [
  { n: "01", label: "Vision & Philosophy", to: "/vision", desc: "Science and mathematics are not memorization.", img: IMG.lab1 },
  { n: "02", label: "Learning Redefined", to: "/learning-redefined", desc: "From chalk-and-talk to Concept Labs.", img: IMG.lab2 },
  { n: "03", label: "STEM Education", to: "/stem", desc: "The foundation of innovation & national development.", img: IMG.lab3 },
  { n: "04", label: "PBL-TDLE Framework", to: "/framework", desc: "Concept Labs · CoPE-App · IDEA Pedagogy.", img: IMG.pblComponents },
  { n: "05", label: "NextGen Training (NTP)", to: "/ntp", desc: "Research mentoring & world-university admissions.", img: IMG.campusWide },
  { n: "06", label: "Outcomes", to: "/outcomes", desc: "Results that prove the framework.", img: IMG.shotB },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const [hover, setHover] = useState(null);

  const [slide, setSlide] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 4500);
    return () => clearInterval(id);
  }, []);

  return (
    <div data-testid="home-page">
      <FloatingQuote />

      {/* 0 · OPENING CHALLENGE — why every classroom must become a Concept Lab */}
      <section className="relative overflow-hidden" data-testid="home-opening-challenge" style={{ backgroundColor: "#0B1B30" }}>
        <div className="absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full blur-3xl opacity-20" style={{ background: "#2AA7D4" }} />
        <div className="absolute -bottom-32 -left-32 w-[28rem] h-[28rem] rounded-full blur-3xl opacity-15" style={{ background: "#F0A13C" }} />
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 pt-32 md:pt-40 pb-16 md:pb-20">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <span className="inline-flex items-center gap-2 font-mono-label text-[11px] tracking-label uppercase text-c-amber border rounded-full px-4 py-2" style={{ borderColor: "rgba(240,161,60,0.5)" }}>
              <span className="animate-pulse">✦</span> A Question for Every School Leader
            </span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="font-display dk-text text-3xl sm:text-4xl md:text-6xl tracking-tight leading-[1.05] mt-8 max-w-5xl"
            data-testid="opening-headline"
          >
            Your Students Aspire to the World's Leading Institutions.{" "}
            <span className="text-c-amber italic">Is Your Classroom Ready to Prepare Them?</span>
          </motion.h2>

          <div className="grid sm:grid-cols-2 gap-5 mt-12 max-w-5xl">
            {[
              { icon: FlaskConical, hex: "#2AA7D4", t: "Leading Research Institutions", q: "Do you want your students conducting research or completing their undergrad in leading institutions / universities immediately after Grade 12?", tags: null },
              { icon: GraduationCap, hex: "#1E9E8A", t: "NEET & JEE Without Coaching", q: "Do you want them to qualify NEET and JEE on their first attempt, without extra coaching?", tags: null },
              { icon: Medal, hex: "#F0A831", t: "International Medals & Scholarships", q: "Do you want them to earn international medals and strengthen their prospects for university admission and scholarships?", tags: "BPhO • AMC • PhysicsBowl • BIOlympiad • Olympiads • AP" },
              { icon: Rocket, hex: "#B79CF5", t: "Research, Publications & Patents", q: "Do you want them to begin research after Grade 12, publish papers and develop patentable innovations?", tags: null },
            ].map((c, i) => {
              const Icon = c.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.35 + i * 0.12 }}
                  className="rounded-2xl border dk-border dk-card p-6 flex items-start gap-4"
                  data-testid={`opening-question-${i}`}
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center border shrink-0" style={{ borderColor: c.hex, backgroundColor: `${c.hex}22` }}>
                    <Icon className="w-5 h-5" style={{ color: c.hex }} />
                  </div>
                  <div>
                    <div className="font-display text-lg tracking-tight" style={{ color: c.hex }}>{c.t}</div>
                    <p className="dk-text-soft leading-relaxed text-[15px] mt-2">{c.q}</p>
                    {c.tags && (
                      <p className="font-mono-label text-[10px] tracking-label uppercase dk-text-muted mt-3">{c.tags}</p>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-12 max-w-5xl rounded-2xl border-l-4 p-6 md:p-8"
            style={{ borderColor: "#F0A13C", backgroundColor: "rgba(255,255,255,0.05)" }}
            data-testid="opening-punchline"
          >
            <p className="font-display text-xl md:text-2xl dk-text leading-snug tracking-tight">
              Every one of these doors opens with a single ability — <span className="text-c-amber">advanced problem-solving</span>.
            </p>
            <p className="font-display text-xl md:text-2xl dk-text-soft leading-snug tracking-tight mt-3">
              That demands a transformation of the classroom itself…
            </p>
            <p className="font-display text-2xl md:text-3xl leading-snug tracking-tight mt-3">
              <span className="text-c-amber font-semibold">THECREATOR Education has achieved it.</span>
            </p>
            <div className="flex items-center gap-2 mt-6 dk-text-muted font-mono-label text-[10px] tracking-label uppercase">
              Here is how <ArrowRight className="w-3.5 h-3.5 rotate-90 animate-bounce" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* 1 · HERO — clear explanation + purposeful CTAs */}
      <section ref={heroRef} className="relative pt-16 md:pt-24 pb-16 md:pb-24 overflow-hidden" data-testid="hero">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} data-testid="home-wordmark">
              <div className="inline-flex items-baseline gap-3 border-b border-[#0B1F33]/15 pb-3">
                <span className="font-display text-2xl md:text-3xl font-bold tracking-tight text-logo-blue">THECREATOR</span>
                <span className="font-mono-label text-[11px] tracking-label uppercase text-logo-blue">Education</span>
              </div>
              <p className="font-display italic text-ink/70 text-base md:text-lg tracking-tight mt-3 mb-6" data-testid="hero-tagline">
                Where Future Researchers Are Nurtured
              </p>
            </motion.div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
              <span className="inline-block font-mono-label text-[11px] tracking-label uppercase text-accent bg-accent/10 px-4 py-2 rounded-full" data-testid="hero-kicker">
                A Complete K–12 STEM Transformation System
              </span>
            </motion.div>
            <KineticHeading
              lines={["Turn Every", "Classroom into", "a Concept Lab"]}
              delay={0.3}
              className="font-display text-ink text-5xl sm:text-6xl md:text-7xl lg:text-[5.2rem] tracking-tight leading-[0.98] mt-6"
            />
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.85 }}
              className="text-[#0B1F33]/65 text-lg md:text-xl max-w-xl mt-8 leading-relaxed"
              data-testid="hero-support"
            >
              THECREATOR Education helps schools develop every concept through teacher demonstrations, student experiments and structured problem-solving — making learning <span className="text-ink font-medium">visible, measurable and verifiable</span>.
            </motion.p>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.9, delay: 1 }}
              className="font-display italic text-accent text-base md:text-lg mt-5"
              data-testid="hero-philosophy"
            >
              “If Innovation Is the Goal, Problem-Solving Must Be the Curriculum.”
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.1 }}
              className="flex flex-wrap items-center gap-4 mt-10"
            >
              <Link to="/framework" data-testid="hero-explore-framework" className="group inline-flex items-center gap-2 bg-accent text-white px-7 py-4 font-mono-label text-[11px] tracking-label uppercase font-medium hover:bg-white transition-colors rounded-full">
                Explore the PBL-TDLE Framework <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
              </Link>
              <Link to="/enroll" data-testid="hero-partner-cta" className="group inline-flex items-center gap-2 border border-[#0B1F33]/20 text-ink px-7 py-4 font-mono-label text-[11px] tracking-label uppercase hover:bg-[#0B1F33] hover:text-white transition-colors rounded-full">
                Bring PBL-TDLE to Your School <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
              </Link>
              <Link to="/outcomes" data-testid="hero-outcomes-link" className="group inline-flex items-center gap-2 text-[#0B1F33]/70 hover:text-accent transition-colors font-mono-label text-[11px] tracking-label uppercase px-2 py-2">
                View Proven Outcomes <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
              </Link>
            </motion.div>
            <motion.a
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.3 }}
              href="https://www.the-creator.net"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-school-link"
              className="group inline-flex items-center gap-2 mt-6 text-[#0B1F33]/55 hover:text-accent transition-colors text-sm"
            >
              Visit The Creator International School — The First PBL-TDLE School <ArrowUpRight className="w-3.5 h-3.5 group-hover:rotate-45 transition-transform" />
            </motion.a>
          </div>

          <motion.div
            style={{ y: bgY }}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}
            className="relative"
          >
            <div className="relative aspect-[4/3] overflow-hidden rounded-2xl border border-[#0B1F33]/10 shadow-2xl shadow-black/10 bg-[#050B14]" data-testid="hero-slideshow">
              <AnimatePresence mode="wait">
                <motion.img
                  key={slide}
                  src={HERO_SLIDES[slide]}
                  alt={SLIDE_CAPTIONS[slide]}
                  style={{ scale: bgScale }}
                  initial={{ opacity: 0, scale: 1.05 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 1.1, ease: "easeOut" }}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </AnimatePresence>
              <img src={HERO_SLIDES[(slide + 1) % HERO_SLIDES.length]} alt="" className="hidden" />
              {/* caption */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/75 to-transparent pt-12 pb-4 px-5" data-testid="hero-slide-caption">
                <p className="text-white text-sm leading-snug">{SLIDE_CAPTIONS[slide]}</p>
              </div>
              <div className="absolute top-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-black/40 backdrop-blur-md rounded-full px-3 py-2" data-testid="hero-slide-dots">
                {HERO_SLIDES.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setSlide(i)}
                    aria-label={`Show slide ${i + 1}`}
                    data-testid={`hero-slide-dot-${i}`}
                    className={`transition-all rounded-full ${i === slide ? "bg-accent w-6 h-1.5" : "bg-white/50 hover:bg-white/80 w-1.5 h-1.5"}`}
                  />
                ))}
              </div>
            </div>
            <div className="absolute -bottom-5 -left-5 bg-white border border-[#0B1F33]/10 rounded-2xl shadow-xl px-6 py-4 hidden sm:block">
              <div className="font-display text-3xl text-accent">100%</div>
              <div className="font-mono-label text-[10px] tracking-label uppercase text-[#0B1F33]/50">Cambridge Board Pass Rate</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 1b · WHY INNOVATION MUST BE THE GOAL — highlighted */}
      <section className="relative overflow-hidden border-t border-white/10" data-testid="home-innovation-goal" style={{ backgroundColor: "#0B1B30" }}>
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full blur-3xl opacity-20" style={{ background: "#F0A13C" }} />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full blur-3xl opacity-15" style={{ background: "#2AA7D4" }} />
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="flex items-center gap-3">
            <span className="text-c-amber text-xl leading-none animate-pulse">✦</span>
            <span className="font-mono-label text-[11px] tracking-label uppercase text-c-amber">If Innovation Is the Goal…</span>
          </div>
          <Reveal>
            <h2 className="font-display text-3xl md:text-5xl dk-text mt-8 tracking-tight leading-tight max-w-4xl">
              The future will not be shaped by those who only know existing answers.
            </h2>
            <p className="font-display text-xl md:text-2xl text-c-amber italic mt-4 tracking-tight">It will be shaped by those who can:</p>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10 max-w-6xl">
            {[
              "Identify problems that have not yet been solved",
              "Question existing explanations",
              "Apply knowledge in unfamiliar situations",
              "Investigate and develop evidence-based solutions",
              "Create new technologies, discoveries and enterprises",
              "Address challenges in health, energy, environment and society",
            ].map((b, i) => (
              <Reveal key={b} delay={(i % 3) * 0.06}>
                <div className="h-full rounded-2xl border dk-border dk-card p-5 flex items-start gap-3" data-testid={`innovation-ability-${i}`}>
                  <CheckCircle2 className="w-5 h-5 text-c-teal shrink-0 mt-0.5" />
                  <p className="dk-text-soft leading-relaxed text-[15px]">{b}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <p className="font-display text-xl md:text-2xl dk-text tracking-tight leading-snug mt-10 max-w-4xl">
              Education must prepare students not only to enter the existing world — <span className="text-c-amber">but to improve it</span>.
            </p>
            <p className="dk-text-soft text-lg mt-3">Innovation begins with the ability to solve unfamiliar problems.</p>
          </Reveal>

          {/* Why innovation should be the goal */}
          <Reveal delay={0.12}>
            <div className="mt-12 rounded-3xl border-2 p-8 md:p-12 max-w-5xl relative overflow-hidden" style={{ borderColor: "rgba(240,161,60,0.5)", backgroundColor: "rgba(255,255,255,0.04)" }} data-testid="innovation-why-panel">
              <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full blur-3xl opacity-20" style={{ background: "#F0A13C" }} />
              <div className="relative">
                <h3 className="font-display text-2xl md:text-3xl text-c-amber tracking-tight leading-snug">
                  Why should innovation be the goal of education?
                </h3>
                <p className="dk-text-soft text-base md:text-lg leading-relaxed mt-6">
                  Because our children will enter a world facing problems for which <span className="dk-text font-medium">prepared answers may not exist</span>. Challenges in health, energy, climate, technology and society cannot be addressed simply by reproducing information from textbooks.
                </p>
                <p className="dk-text-soft text-base md:text-lg leading-relaxed mt-4">
                  Countries progress when their young people can identify problems, investigate them and create evidence-based solutions. Therefore, education must prepare students not only to participate in the existing world — but also to <span className="dk-text font-medium">improve it</span>.
                </p>
                <div className="mt-8 pt-6 border-t dk-border">
                  <p className="font-display text-xl md:text-2xl dk-text tracking-tight leading-snug">
                    “Innovation begins with <span className="text-c-amber">problem-solving</span>. And problem-solving begins with <span className="text-c-amber">deeply developed concepts</span>.”
                  </p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-white/10 bg-[#070d18]">
        <Marquee items={["Problem Solving", "Concept Labs", "Research", "Innovation", "Experiments"]} />
      </div>

      {/* 2 · CREDIBILITY STRIP */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="highlights">
        <div className="grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-7">
            <SectionLabel n="✦">The Movement</SectionLabel>
            <Reveal>
              <h2 className="font-display text-3xl md:text-5xl text-white leading-tight tracking-tight mt-8 font-light">
                K–12 STEM schools where problem solving is the daily language of learning.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-white/60 text-lg leading-relaxed mt-8 max-w-2xl">
                We go beyond traditional chalk-and-talk teaching by transforming classrooms into advanced scientific Concept Labs, where students investigate and verify every concept through daily experiments — and are systematically trained to apply them in advanced problem solving.
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-5 md:pl-10 md:border-l border-white/10">
            <div className="grid grid-cols-2 gap-10">
              <Stat value={100} suffix="%" label="Cambridge Board Pass Rate" />
              <Stat value={5000} suffix="+" label="Structured Experiments" />
              <Stat value={10000} suffix="+" label="Application-Based Problems" />
              <Stat value={1500} suffix="+" label="Apparatus & Experimental Resources" />
            </div>
            <p className="text-white/40 text-xs leading-relaxed mt-8" data-testid="stats-note">
              Figures from The Creator International School, Khamgaon — the first PBL-TDLE school — across cohorts from 2015 to 2025.
            </p>
          </div>
        </div>
      </section>

      {/* 3 · THE PROBLEM — From Syllabus Completion to Assured Learning */}
      <section className="border-t border-white/10" data-testid="home-why">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="01">Why Schools Need This</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-8 tracking-tight leading-tight font-light max-w-4xl">
            From syllabus completion to <span className="text-accent">assured learning</span>.
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
            {[
              "Content delivery does not guarantee learning.",
              "Teachers cannot know who remained attentive.",
              "Familiarity with a concept does not create mastery.",
              "Problem-solving is impossible without concept development.",
            ].map((p, i) => (
              <Reveal key={p} delay={i * 0.06}>
                <div className="h-full border border-white/10 rounded-2xl bg-[#070d18] p-6" data-testid={`home-problem-${i}`}>
                  <XCircle className="w-5 h-5 text-c-red" />
                  <p className="font-display text-lg text-white/85 leading-snug tracking-tight mt-4">{p}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-10 rounded-2xl border dk-border dk-panel p-6 md:p-8" data-testid="home-pbl-response">
              <div className="font-mono-label text-[11px] tracking-label uppercase text-c-amber">The PBL-TDLE Response</div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mt-4">
                {["Every Student", "Every Concept", "Experimentally Developed", "Continuously Assessed", "Independently Applied"].map((s, i, arr) => (
                  <span key={s} className="inline-flex items-center gap-3">
                    <span className="font-display text-lg md:text-2xl dk-text tracking-tight">{s}</span>
                    {i < arr.length - 1 && <ArrowRight className="w-4 h-4 text-c-teal shrink-0" />}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 4 · THE TRANSFORMATION — comparison at a glance */}
      <section className="border-t border-white/10 bg-[#070d18]" data-testid="home-transformation">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="02">The Transformation</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-8 tracking-tight leading-tight font-light max-w-4xl">
            Understand PBL-TDLE in seconds.
          </h2>
          <div className="mt-12 border border-white/10 rounded-2xl overflow-hidden max-w-4xl">
            <div className="grid grid-cols-2 dk-panel font-mono-label text-[11px] tracking-label uppercase">
              <div className="p-4 border-r dk-border dk-text-muted">Traditional Classroom</div>
              <div className="p-4 text-c-amber">PBL-TDLE Concept Lab</div>
            </div>
            {TRANSFORM_ROWS.map((r, i) => (
              <div key={r.trad} className={`grid grid-cols-2 text-sm md:text-base border-t border-white/10 ${i % 2 ? "bg-[#050B14]" : "bg-[#070d18]"}`} data-testid={`transform-row-${i}`}>
                <div className="p-4 border-r border-white/10 text-white/55 leading-relaxed">{r.trad}</div>
                <div className="p-4 text-white leading-relaxed flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-c-teal shrink-0" />{r.pbl}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5 · ONE CONNECTED SYSTEM */}
      <section className="border-t border-white/10" data-testid="home-system">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="03">One Connected System</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-8 tracking-tight leading-tight font-light max-w-4xl">
            One framework. <span className="text-accent">Every stage of learning connected.</span>
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5 mt-12">
            {SYSTEM_PARTS.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.t} delay={i * 0.06}>
                  <Link to={s.to} className="group block h-full border border-white/10 rounded-2xl bg-[#070d18] p-6 hover:border-accent/60 transition-colors" data-testid={`system-part-${i}`}>
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center border" style={{ borderColor: s.hex, backgroundColor: `${s.hex}18` }}>
                      <Icon className="w-5 h-5" style={{ color: s.hex }} />
                    </div>
                    <div className="font-display text-lg text-white mt-5 tracking-tight leading-snug group-hover:text-accent transition-colors">{s.t}</div>
                    <p className="text-white/55 text-sm leading-relaxed mt-2">{s.d}</p>
                    <span className="inline-flex items-center gap-1.5 font-mono-label text-[10px] tracking-label uppercase text-white/40 mt-4 group-hover:text-accent transition-colors">
                      Explore <ArrowUpRight className="w-3 h-3" />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 6 · OUTCOMES PROVE THE FRAMEWORK */}
      <section className="border-t border-white/10 bg-[#070d18]" data-testid="home-outcomes">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <SectionLabel n="04">Evidence</SectionLabel>
              <h2 className="font-display text-3xl md:text-5xl text-white mt-8 tracking-tight leading-tight font-light max-w-3xl">
                The outcomes <span className="text-accent">prove the framework</span>.
              </h2>
            </div>
            <Link to="/outcomes" data-testid="home-outcomes-cta" className="group inline-flex items-center gap-2 border border-white/20 text-white px-6 py-3.5 rounded-full font-mono-label text-[11px] tracking-label uppercase hover:bg-accent hover:border-accent transition-colors">
              See Every Result & Story <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {OUTCOME_CARDS.map((c, i) => {
              const Icon = c.icon;
              return (
                <Reveal key={c.t} delay={(i % 3) * 0.06}>
                  <Link to="/outcomes" className="group block h-full border border-white/10 rounded-2xl bg-[#050B14] p-7 hover:border-accent/60 transition-colors" data-testid={`home-outcome-${i}`}>
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center border" style={{ borderColor: c.hex, backgroundColor: `${c.hex}18` }}>
                      <Icon className="w-5 h-5" style={{ color: c.hex }} />
                    </div>
                    <div className="font-display text-xl text-white mt-5 tracking-tight leading-snug group-hover:text-accent transition-colors">{c.t}</div>
                    <p className="text-white/55 text-sm leading-relaxed mt-2">{c.d}</p>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 7 · AUDIENCE ENTRY POINTS */}
      <section className="border-t border-white/10" data-testid="home-audiences">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="05">Where Should You Start?</SectionLabel>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
            {AUDIENCES.map((a, i) => {
              const Icon = a.icon;
              return (
                <Reveal key={a.who} delay={i * 0.06}>
                  <Link to={a.to} className="group block h-full border border-white/10 rounded-2xl bg-[#070d18] p-7 hover:border-accent/60 transition-colors" data-testid={`audience-${i}`}>
                    <div className="flex items-center justify-between">
                      <div className="w-11 h-11 rounded-xl flex items-center justify-center border" style={{ borderColor: a.hex, backgroundColor: `${a.hex}18` }}>
                        <Icon className="w-5 h-5" style={{ color: a.hex }} />
                      </div>
                      <ArrowUpRight className="w-5 h-5 text-white/30 group-hover:text-accent group-hover:rotate-45 transition-all" />
                    </div>
                    <div className="font-mono-label text-[10px] tracking-label uppercase text-white/45 mt-6">{a.who}</div>
                    <div className="font-display text-xl text-white mt-2 tracking-tight leading-snug group-hover:text-accent transition-colors">{a.label}</div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 8 · TESTIMONIALS with identities */}
      <section className="border-t border-white/10 bg-[#070d18]" data-testid="home-testimonials">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionLabel n="06">Voices From Our Community</SectionLabel>
            <Link to="/outcomes" data-testid="home-testimonials-link" className="group inline-flex items-center gap-2 font-mono-label text-[11px] tracking-label uppercase text-white/50 hover:text-accent transition-colors">
              Read All Testimonials <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-px bg-white/10 mt-12 border border-white/10">
            {HOME_TESTIMONIALS.map((t, i) => (
              <Reveal key={t.a} delay={i * 0.08} className="bg-[#050B14] p-8 md:p-10 flex flex-col">
                <span className="font-display text-6xl text-accent leading-none">“</span>
                <p className="text-white/80 leading-relaxed -mt-3 flex-1">{t.q}</p>
                <div className="mt-6 pt-5 border-t border-white/10">
                  <div className="font-display text-lg text-white tracking-tight">{t.a}</div>
                  <div className="font-mono-label text-[10px] tracking-label uppercase text-white/50 mt-1.5">{t.r}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 9 · EXPLORE — editorial index */}
      <section className="border-t border-white/10" data-testid="section-index">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-16">
          <SectionLabel n="◆">Explore the Framework</SectionLabel>
        </div>
        <div className="relative">
          {PREVIEWS.map((p) => (
            <Link
              key={p.to}
              to={p.to}
              data-testid={`preview-${p.n}`}
              onMouseEnter={() => setHover(p.n)}
              onMouseLeave={() => setHover(null)}
              className="group block border-t border-white/10 last:border-b"
            >
              <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-8 md:py-10 flex items-center justify-between gap-6">
                <div className="flex items-baseline gap-6 md:gap-10">
                  <span className="font-mono-label text-xs text-accent">{p.n}</span>
                  <div>
                    <h3 className="font-display text-3xl md:text-5xl text-white/85 group-hover:text-accent transition-colors tracking-tight">{p.label}</h3>
                    <p className="text-white/40 text-sm mt-2 max-w-md">{p.desc}</p>
                  </div>
                </div>
                <ArrowUpRight className="w-6 h-6 md:w-8 md:h-8 text-white/40 group-hover:text-accent group-hover:rotate-45 transition-all shrink-0" />
              </div>
            </Link>
          ))}
          <AnimatePresence>
            {hover && (
              <motion.div
                key={hover}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3 }}
                className="hidden lg:block fixed top-1/2 right-24 -translate-y-1/2 z-[5] w-80 h-96 pointer-events-none spotlight-clip overflow-hidden border border-white/10"
              >
                <img src={PREVIEWS.find((p) => p.n === hover)?.img} alt="" className="w-full h-full object-cover" />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* 10 · PULL QUOTE */}
      <section className="bg-cream text-ink" data-testid="pull-quote">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-40">
          <SectionLabel n="✦" dark>Our Belief</SectionLabel>
          <Reveal>
            <blockquote className="font-display text-3xl md:text-5xl lg:text-6xl text-ink leading-[1.1] tracking-tight mt-10 max-w-6xl font-light">
              “{PULL_QUOTE}”
            </blockquote>
          </Reveal>
        </div>
      </section>

      {/* 11 · IN THE NEWS */}
      <section className="border-t border-white/10 bg-[#050B14]" data-testid="home-media">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <SectionLabel n="◆">In the News</SectionLabel>
              <h2 className="font-display text-3xl md:text-5xl text-white mt-8 tracking-tight leading-tight font-light max-w-3xl">
                How PBL-TDLE has been featured.
              </h2>
            </div>
            <p className="font-mono-label text-[11px] tracking-label uppercase text-white/40 max-w-xs">
              Press coverage and thought leadership on India's Concept-Lab movement.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 mt-12 border border-white/10">
            {PRESS.map((p, i) => (
              <Reveal key={i} delay={(i % 3) * 0.06}>
                <a
                  href={p.href}
                  target={p.href !== "#" ? "_blank" : undefined}
                  rel={p.href !== "#" ? "noreferrer" : undefined}
                  data-testid={`press-item-${i}`}
                  className="block bg-[#050B14] p-8 h-full hover:bg-[#070d18] transition-colors group"
                >
                  <div className="font-mono-label text-[11px] tracking-label uppercase text-accent">{p.outlet}</div>
                  <h3 className="font-display text-xl md:text-2xl text-white mt-4 tracking-tight leading-snug group-hover:text-accent transition-colors">{p.headline}</h3>
                  <span className="inline-flex items-center gap-2 font-mono-label text-[10px] tracking-label uppercase text-white/40 mt-6 group-hover:text-white/70 transition-colors">
                    Read coverage <ArrowUpRight className="w-3.5 h-3.5" />
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 12 · FINAL PARTNERSHIP INVITATION */}
      <CTASection />
    </div>
  );
}
